import React, { useContext, useState } from 'react';

import { Segmented, DatePicker } from 'antd';
import { Formik, Field } from 'formik';
import * as Yup from 'yup';

import styled from 'styled-components';

import { getMoment, getNow, showDateNoTime } from '@libs/NwMoment';

import { useLayoutContext } from '@contexts/LayoutContext';
import { UserContext } from '@contexts/UserContext';

import { useAddPackage, useAddPlaylistToPackage } from '@hooks/API/Packages/UsePackages';
import useConfigurations from '@hooks/Configurations/UseConfigurations';

import { NwCancelButton, NwSaveButton } from '@components/Gui/NwButton';
import { NwFakeFormItem } from '@components/Gui/NwForm/NwFakeFormWrapper';
import { NWDepartmentSelector, NWInput, NWProjectSelector, NWSwitch } from '@components/Gui/NwForm/NwFormItems';
import { NwForm, NwFormButtonsBar } from '@components/Gui/NwForm/NwFormWrapper';
import NwIcon from '@components/Gui/NwIcon';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const StyledSegmented = styled(Segmented)`
    margin-bottom: 2rem;
    .expiration-period-item {
        padding-top: 0.75rem;
        padding-bottom: 0.25rem;
        h1 {
            font-size: ${(props) => props.theme.FontSizeSm};
            font-weight: bold;
            margin: 0;
            line-height: 1.1em;
        }
        div {
            padding-top: 0.25rem;
            line-height: 1.6em;
        }
        &.expiration-period-item-custom {
            div {
                text-decoration: underline;
            }
        }
        &.expiration-period-item-custom-empty {
            div {
                padding-top: 0;
                font-size: 1.25rem;
            }
        }
    }
`;

/**
 * Form di creazione package: crea il package e ci mette dentro i book passati.
 * Non sa dove sta: lo usano sia AddPackageDrawer, che lo apre in un drawer, sia il
 * selettore di book, che lo apre dentro al pannello.
 */
const PackageForm = ({ bookIDs, packageName, projectId, onAddedPackage, onClose, onTouchForm }) => {
    const { currentUser } = useContext(UserContext);
    const { isMobile } = useLayoutContext();
    const { mutateAsync: onAddPackage } = useAddPackage();
    const { mutateAsync: onAddPlaylistToPackage } = useAddPlaylistToPackage();
    const configurations = useConfigurations();

    const [daysToExpire, setDaysToExpire] = useState(
        configurations.packageExpirationDays ? configurations.packageExpirationDays : 30
    );
    const [expirationCustomDate, setExpirationCustomDate] = useState();
    const [datePickerOpen, setDatePickerOpen] = useState(false);

    const initialValues = {
        Title: packageName || `Package by ${currentUser.Name} - ${getNow().format('MMM DD, HH:mm')}`,
        IsInteractive: true,
        DepartmentCreated: currentUser.ActiveContext.depId,
        ProjectID: projectId ? projectId : '',
    };

    // Il guard sulle modifiche non salvate lo ha solo il drawer.
    const setTouchForm = (value) => {
        if (onTouchForm) {
            onTouchForm(value);
        }
    };

    const savePackage = async (values, setSubmitting) => {
        const expdate =
            daysToExpire === 0
                ? expirationCustomDate.format('YYYY-MM-DD')
                : getMoment().add(daysToExpire, 'days').format('YYYY-MM-DD');

        const data = {
            name: values.Title,
            isInteractive: values.IsInteractive,
            DepartmentCreated: values.DepartmentCreated,
            ProjectID: values.ProjectID,
            ExpirationDate: expdate,
        };

        try {
            const pack = await onAddPackage({ data });
            setTouchForm(false);

            if (bookIDs && bookIDs.length > 0) {
                for (const bookID of bookIDs) {
                    const data = {
                        packageID: pack.ID,
                        playlistID: bookID,
                    };
                    await onAddPlaylistToPackage({
                        data,
                        isActivePackage: true,
                    });
                }
            }

            if (onAddedPackage) {
                onAddedPackage(pack.ID);
            }
            setSubmitting(false);
            onClose();
        } catch (error) {
            console.error('Add New Package Error => ', error);
            setSubmitting(false);
        }
    };

    const disabledDate = (current) => {
        // Can not select days before today and today
        return current && current < getMoment().endOf('day');
    };

    const notValidExpDate = () => {
        return daysToExpire === 0 && !expirationCustomDate;
    };

    // All options - filtered for mobile to show only 7, 30, and 90 days
    const allOptions = [
        {
            label: (
                <div className="expiration-period-item">
                    <h1>7 days</h1>
                    <div>{getMoment().add(7, 'days').format('MMM DD')}</div>
                </div>
            ),
            value: 7,
        },
        {
            label: (
                <div className="expiration-period-item">
                    <h1>30 days</h1>
                    <div>{getMoment().add(30, 'days').format('MMM DD')}</div>
                </div>
            ),
            value: 30,
        },
        {
            label: (
                <div className="expiration-period-item">
                    <h1>60 days</h1>
                    <div>{getMoment().add(60, 'days').format('MMM DD')}</div>
                </div>
            ),
            value: 60,
        },
        {
            label: (
                <div className="expiration-period-item">
                    <h1>90 days</h1>
                    <div>{getMoment().add(90, 'days').format('MMM DD')}</div>
                </div>
            ),
            value: 90,
        },
        {
            label: expirationCustomDate ? (
                <div className="expiration-period-item expiration-period-item-custom">
                    <h1>Custom</h1>
                    <div onClick={() => setDatePickerOpen(true)}>{showDateNoTime(expirationCustomDate)}</div>
                </div>
            ) : (
                <div className="expiration-period-item expiration-period-item-custom-empty">
                    <h1>Custom</h1>
                    <div>
                        <NwIcon
                            pointer
                            icon={byPrefixAndName.fasl['calendar']}
                            onClick={() => setDatePickerOpen(true)}
                        />
                    </div>
                </div>
            ),
            value: 0,
        },
    ];

    // Filter options for mobile (7, 30, 60, 90 days - no custom)
    const options = isMobile ? allOptions.filter((option) => [7, 30, 60, 90].includes(option.value)) : allOptions;

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={Yup.object().shape({
                Title: Yup.string().required('Content is required'),
            })}
            onSubmit={(values, { setSubmitting }) => {
                setSubmitting(true);
                savePackage(values, setSubmitting);
            }}
        >
            {({ values, handleSubmit, isSubmitting }) => (
                <NwForm values={values} onTouchForm={setTouchForm} onFinish={handleSubmit} layout="vertical">
                    <Field component={NWInput} label="Package Title" name="Title" type="text" value={values.Title} />
                    {!projectId && (
                        <Field
                            component={NWDepartmentSelector}
                            label="Department"
                            name="DepartmentCreated"
                            value={values.DepartmentCreated}
                            activeContext
                        />
                    )}
                    <Field
                        component={NWSwitch}
                        label="Is Interactive"
                        name="IsInteractive"
                        defaultChecked={values.IsInteractive ? true : false}
                        checked={Boolean(values.IsInteractive)}
                    />
                    <NwFakeFormItem
                        label="Expiration Date"
                        control={
                            <div className="expiration-wrapper">
                                <StyledSegmented
                                    options={options}
                                    value={isMobile && daysToExpire === 0 ? 30 : daysToExpire}
                                    onChange={(val) => {
                                        setDaysToExpire(val);
                                        if (val === 0 && !expirationCustomDate) {
                                            setDatePickerOpen(true);
                                        }
                                    }}
                                />
                                {daysToExpire === 0 && !isMobile && (
                                    <DatePicker
                                        value={expirationCustomDate}
                                        allowClear={false}
                                        style={{ visibility: 'hidden', width: 0 }}
                                        open={datePickerOpen}
                                        onChange={(val) => {
                                            setExpirationCustomDate(val);
                                            setDatePickerOpen(false);
                                        }}
                                        disabledDate={disabledDate}
                                    />
                                )}
                            </div>
                        }
                    />
                    {!projectId && <Field component={NWProjectSelector} name="ProjectID" value={values.ProjectID} />}
                    <NwFormButtonsBar
                        left={
                            <NwCancelButton
                                disabled={isSubmitting}
                                onClick={() => {
                                    setTouchForm(false);
                                    onClose();
                                }}
                            />
                        }
                        right={
                            <NwSaveButton
                                label="Create"
                                htmlType="submit"
                                disabled={isSubmitting || notValidExpDate()}
                                loading={isSubmitting}
                            />
                        }
                    />
                </NwForm>
            )}
        </Formik>
    );
};

export default PackageForm;
